import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  Search, 
  Upload, 
  Image as ImageIcon, 
  Loader2,
  Check,
  Download,
  ExternalLink,
  Folder,
  Wand2,
  Eye,
  Layers
} from 'lucide-react';

interface PexelsPhoto {
  id: number;
  url: string;
  medium_url: string;
  small_url: string;
  photographer: string;
  photographer_url: string;
  alt: string;
  download_url: string;
}

interface FeaturedImageSelectorProps {
  onImageSelected: (imageUrl: string) => void;
  onAltTextGenerated?: (altText: string) => void;
  currentImageUrl?: string;
  articleTitle?: string;
  articleExcerpt?: string;
  keywords?: string;
}

export const FeaturedImageSelector: React.FC<FeaturedImageSelectorProps> = ({
  onImageSelected,
  onAltTextGenerated,
  currentImageUrl,
  articleTitle,
  articleExcerpt,
  keywords
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [pexelsPhotos, setPexelsPhotos] = useState<PexelsPhoto[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedImageId, setSelectedImageId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [imageSize, setImageSize] = useState<'thumbnail' | 'medium' | 'large' | 'custom'>('large');
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [customWidth, setCustomWidth] = useState<string>('1024');
  const [customHeight, setCustomHeight] = useState<string>('768');
  const [batchGenerating, setBatchGenerating] = useState(false);
  const [generatedBatch, setGeneratedBatch] = useState<{ size: string; url: string }[]>([]);
  const [promptMode, setPromptMode] = useState<'content' | 'custom'>('content');
  const [customPrompt, setCustomPrompt] = useState('');

  const searchPexelsImages = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('pexels-image-search', {
        body: { query: searchQuery, per_page: 12 }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      setPexelsPhotos(data.photos || []);
      toast.success(`Found ${data.photos?.length || 0} images`);
    } catch (error) {
      console.error('Error searching Pexels:', error);
      toast.error('Failed to search images. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePexelsImageSelect = (photo: PexelsPhoto) => {
    setSelectedImageId(`pexels-${photo.id}`);
    onImageSelected(photo.url);
    toast.success('Image selected successfully!');
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchPexelsImages();
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));
    
    if (imageFiles.length > 0) {
      handleFileUpload(imageFiles[0]);
    } else {
      toast.error('Please drop an image file');
    }
  };

  const handleFileUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) { // 5MB limit
      toast.error('File size must be less than 5MB');
      return;
    }

    setLoading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      
      const { data, error } = await supabase.storage
        .from('images')
        .upload(fileName, file);

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('images')
        .getPublicUrl(fileName);

      onImageSelected(publicUrl);
      setSelectedImageId(`upload-${fileName}`);
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file);
    }
  };

  const generateAIImage = async (sizeOverride?: string, customDimensions?: { width: number; height: number }) => {
    // For custom prompt mode, we don't need article title
    if (promptMode === 'content' && !articleTitle) {
      toast.error('Please enter an article title first');
      return null;
    }
    
    if (promptMode === 'custom' && !customPrompt.trim()) {
      toast.error('Please enter a custom prompt');
      return null;
    }

    const actualSize = sizeOverride || imageSize;
    
    try {
      const { data, error } = await supabase.functions.invoke('generate-article-image', {
        body: {
          articleTitle: promptMode === 'content' ? articleTitle : undefined,
          excerpt: promptMode === 'content' ? articleExcerpt : undefined,
          keywords: promptMode === 'content' ? keywords : undefined,
          customPrompt: promptMode === 'custom' ? customPrompt : undefined,
          size: actualSize,
          customDimensions
        }
      });

      if (error) throw error;
      if (!data.success) throw new Error(data.error);

      // Auto-populate alt text if callback is provided
      if (data.suggestedAlt && onAltTextGenerated) {
        onAltTextGenerated(data.suggestedAlt);
      }

      return data.imageUrl;
    } catch (error) {
      console.error('Error generating AI image:', error);
      throw error;
    }
  };

  const handleSingleGeneration = async () => {
    setGeneratingAI(true);
    try {
      const customDimensions = imageSize === 'custom' 
        ? { width: parseInt(customWidth), height: parseInt(customHeight) }
        : undefined;

      const imageUrl = await generateAIImage(undefined, customDimensions);
      if (imageUrl) {
        setPreviewImage(imageUrl);
      }
    } catch (error) {
      toast.error('Failed to generate AI image. Please try again.');
    } finally {
      setGeneratingAI(false);
    }
  };

  const handleBatchGeneration = async () => {
    setBatchGenerating(true);
    setGeneratedBatch([]);
    
    try {
      const sizes = ['thumbnail', 'medium', 'large'];
      const results: { size: string; url: string }[] = [];
      
      toast.info('Generating all image sizes...', {
        description: 'This may take a moment'
      });

      for (const size of sizes) {
        try {
          const imageUrl = await generateAIImage(size);
          if (imageUrl) {
            results.push({ size, url: imageUrl });
          }
        } catch (error) {
          console.error(`Error generating ${size} image:`, error);
          toast.error(`Failed to generate ${size} image`);
        }
      }

      if (results.length > 0) {
        setGeneratedBatch(results);
        toast.success(`Generated ${results.length} image sizes successfully!`);
      }
    } catch (error) {
      toast.error('Batch generation failed. Please try again.');
    } finally {
      setBatchGenerating(false);
    }
  };

  const handlePreviewConfirm = () => {
    if (previewImage) {
      onImageSelected(previewImage);
      setSelectedImageId('ai-generated');
      toast.success('Image selected successfully!');
      setPreviewImage(null);
    }
  };

  const handleBatchSelect = (imageUrl: string, size: string) => {
    onImageSelected(imageUrl);
    setSelectedImageId(`batch-${size}`);
    toast.success(`${size.charAt(0).toUpperCase() + size.slice(1)} image selected!`);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="w-5 h-5 text-primary" />
          Featured Image
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="ai" className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="ai">AI Generate</TabsTrigger>
            <TabsTrigger value="pexels">Stock Images</TabsTrigger>
            <TabsTrigger value="upload">Upload</TabsTrigger>
            <TabsTrigger value="media">Media Library</TabsTrigger>
          </TabsList>

          {/* AI Image Generation */}
          <TabsContent value="ai" className="space-y-4">
            <div className="space-y-6 py-4">
              <div className="text-center space-y-2">
                <div className="flex justify-center">
                  <div className="p-4 bg-primary/10 rounded-full">
                    <Wand2 className="w-12 h-12 text-primary" />
                  </div>
                </div>
                <h3 className="text-lg font-semibold">AI-Generated Featured Image</h3>
                <p className="text-muted-foreground max-w-md mx-auto text-sm">
                  Generate unique, professional images using AI - from your article content or a custom prompt.
                </p>
              </div>
              
              {/* Prompt Mode Selector */}
              <div className="space-y-3 max-w-2xl mx-auto">
                <Label className="text-sm font-medium">Generation Mode</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={promptMode === 'content' ? 'default' : 'outline'}
                    onClick={() => setPromptMode('content')}
                    disabled={generatingAI || batchGenerating}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <span className="font-semibold text-xs">From Content</span>
                    <span className="text-xs opacity-70">Use article details</span>
                  </Button>
                  <Button
                    type="button"
                    variant={promptMode === 'custom' ? 'default' : 'outline'}
                    onClick={() => setPromptMode('custom')}
                    disabled={generatingAI || batchGenerating}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <span className="font-semibold text-xs">Custom Prompt</span>
                    <span className="text-xs opacity-70">Describe your image</span>
                  </Button>
                </div>
              </div>
              
              {/* Custom Prompt Input */}
              {promptMode === 'custom' ? (
                <div className="space-y-3 max-w-2xl mx-auto">
                  <Label className="text-sm font-medium">Describe the image you want</Label>
                  <textarea
                    value={customPrompt}
                    onChange={(e) => setCustomPrompt(e.target.value)}
                    placeholder="E.g., A modern office with people collaborating around a whiteboard, bright natural lighting, minimalist design..."
                    className="w-full min-h-[100px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                    disabled={generatingAI || batchGenerating}
                  />
                  <p className="text-xs text-muted-foreground">Be descriptive for best results. Include style, mood, colors, and composition.</p>
                </div>
              ) : (
                <div className="text-center text-sm text-muted-foreground max-w-md mx-auto">
                  {articleTitle ? (
                    <p>Will generate based on: <strong>"{articleTitle}"</strong>{keywords && <>, keywords: <strong>{keywords}</strong></>}</p>
                  ) : (
                    <p className="text-destructive">Please enter an article title first</p>
                  )}
                </div>
              )}
              
              {/* Image Size Selector */}
              <div className="space-y-3 max-w-2xl mx-auto">
                <Label className="text-sm font-medium">Image Size</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Button
                    type="button"
                    variant={imageSize === 'thumbnail' ? 'default' : 'outline'}
                    onClick={() => setImageSize('thumbnail')}
                    disabled={generatingAI || batchGenerating}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <span className="font-semibold text-xs">Thumbnail</span>
                    <span className="text-xs opacity-70">400×300</span>
                  </Button>
                  <Button
                    type="button"
                    variant={imageSize === 'medium' ? 'default' : 'outline'}
                    onClick={() => setImageSize('medium')}
                    disabled={generatingAI || batchGenerating}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <span className="font-semibold text-xs">Medium</span>
                    <span className="text-xs opacity-70">800×600</span>
                  </Button>
                  <Button
                    type="button"
                    variant={imageSize === 'large' ? 'default' : 'outline'}
                    onClick={() => setImageSize('large')}
                    disabled={generatingAI || batchGenerating}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <span className="font-semibold text-xs">Large</span>
                    <span className="text-xs opacity-70">1200×630</span>
                  </Button>
                  <Button
                    type="button"
                    variant={imageSize === 'custom' ? 'default' : 'outline'}
                    onClick={() => setImageSize('custom')}
                    disabled={generatingAI || batchGenerating}
                    className="flex flex-col items-center gap-1 h-auto py-3"
                  >
                    <span className="font-semibold text-xs">Custom</span>
                    <span className="text-xs opacity-70">Your size</span>
                  </Button>
                </div>
              </div>

              {/* Custom Dimensions */}
              {imageSize === 'custom' && (
                <div className="space-y-3 max-w-md mx-auto">
                  <Label className="text-sm font-medium">Custom Dimensions (pixels)</Label>
                  <div className="flex gap-3 items-center">
                    <div className="flex-1">
                      <Label htmlFor="width" className="text-xs text-muted-foreground">Width</Label>
                      <Input
                        id="width"
                        type="number"
                        min="256"
                        max="2048"
                        value={customWidth}
                        onChange={(e) => setCustomWidth(e.target.value)}
                        placeholder="1024"
                        className="mt-1"
                      />
                    </div>
                    <span className="text-muted-foreground mt-6">×</span>
                    <div className="flex-1">
                      <Label htmlFor="height" className="text-xs text-muted-foreground">Height</Label>
                      <Input
                        id="height"
                        type="number"
                        min="256"
                        max="2048"
                        value={customHeight}
                        onChange={(e) => setCustomHeight(e.target.value)}
                        placeholder="768"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground">Range: 256-2048 pixels</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <Button 
                  type="button"
                  onClick={handleSingleGeneration}
                  disabled={generatingAI || batchGenerating || (promptMode === 'content' ? !articleTitle : !customPrompt.trim())}
                  size="lg"
                  className="flex-1"
                >
                  {generatingAI ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Generate Image
                    </>
                  )}
                </Button>
                <Button 
                  type="button"
                  onClick={handleBatchGeneration}
                  disabled={generatingAI || batchGenerating || (promptMode === 'content' ? !articleTitle : !customPrompt.trim())}
                  size="lg"
                  variant="outline"
                  className="flex-1"
                >
                  {batchGenerating ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Generating All...
                    </>
                  ) : (
                    <>
                      <Layers className="w-5 h-5 mr-2" />
                      Generate All Sizes
                    </>
                  )}
                </Button>
              </div>

              {/* Batch Results */}
              {generatedBatch.length > 0 && (
                <div className="space-y-3 max-w-2xl mx-auto">
                  <Label className="text-sm font-medium">Generated Images</Label>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {generatedBatch.map(({ size, url }) => (
                      <div key={size} className="space-y-2">
                        <div className="relative group">
                          <img
                            src={url}
                            alt={`${size} generated image`}
                            className="w-full h-32 object-cover rounded-lg border"
                          />
                           <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              onClick={() => setPreviewImage(url)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              onClick={() => handleBatchSelect(url, size)}
                            >
                              <Check className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <Badge className="w-full justify-center">
                          {size.charAt(0).toUpperCase() + size.slice(1)}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <p className="text-xs text-muted-foreground text-center">
                Powered by Gemini 3 Pro • Creates unique AI-generated images
              </p>
            </div>
          </TabsContent>

          {/* Pexels Image Search */}
          <TabsContent value="pexels" className="space-y-4">
            <div className="flex gap-2">
              <Input
                placeholder="Search for images (e.g., 'business meeting', 'technology')"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                className="flex-1"
              />
              <Button type="button" onClick={searchPexelsImages} disabled={loading}>
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Search className="w-4 h-4" />
                )}
                Search
              </Button>
            </div>

            {pexelsPhotos.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {pexelsPhotos.map((photo) => (
                  <div key={photo.id} className="relative group">
                    <div
                      className={`
                        relative cursor-pointer rounded-lg overflow-hidden border-2 transition-all
                        ${selectedImageId === `pexels-${photo.id}` 
                          ? 'border-primary shadow-lg' 
                          : 'border-transparent hover:border-primary/50'
                        }
                      `}
                      onClick={() => handlePexelsImageSelect(photo)}
                    >
                      <img
                        src={photo.medium_url}
                        alt={photo.alt}
                        className="w-full h-32 object-cover"
                      />
                      {selectedImageId === `pexels-${photo.id}` && (
                        <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                          <Check className="w-6 h-6 text-primary" />
                        </div>
                      )}
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <p className="text-white text-xs truncate">
                          by {photo.photographer}
                        </p>
                      </div>
                    </div>
                    <div className="flex justify-between items-center mt-2">
                      <Badge variant="secondary" className="text-xs">
                        Pexels
                      </Badge>
                      <div className="flex gap-1">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(photo.photographer_url, '_blank')}
                          title="Photographer Profile"
                        >
                          <ExternalLink className="w-3 h-3" />
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => window.open(photo.download_url, '_blank')}
                          title="Download Original"
                        >
                          <Download className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* File Upload */}
          <TabsContent value="upload" className="space-y-4">
            <div
              className={`
                border-2 border-dashed rounded-lg p-8 text-center transition-colors
                ${dragOver 
                  ? 'border-primary bg-primary/5' 
                  : 'border-muted-foreground/25 hover:border-primary/50'
                }
              `}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <Upload className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Upload Featured Image</h3>
              <p className="text-muted-foreground mb-4">
                Drag and drop an image here, or click to select
              </p>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
                id="image-upload"
                disabled={loading}
              />
              <Button
                type="button"
                onClick={() => document.getElementById('image-upload')?.click()}
                disabled={loading}
                variant="outline"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Choose File
                  </>
                )}
              </Button>
              <p className="text-xs text-muted-foreground mt-2">
                Supports: JPG, PNG, GIF (max 5MB)
              </p>
            </div>
          </TabsContent>

          {/* Media Library */}
          <TabsContent value="media" className="space-y-4">
            <div className="text-center py-8">
              <Folder className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">Media Library</h3>
              <p className="text-muted-foreground mb-4">
                Browse your uploaded media files
              </p>
              <Button variant="outline" disabled>
                <Folder className="w-4 h-4 mr-2" />
                Coming Soon
              </Button>
            </div>
          </TabsContent>
        </Tabs>

        {/* Current Image Preview */}
        {currentImageUrl && (
          <div className="mt-6 p-4 border rounded-lg">
            <h4 className="font-medium mb-2">Current Featured Image</h4>
            <img
              src={currentImageUrl}
              alt="Current featured image"
              className="w-full h-32 object-cover rounded"
            />
          </div>
        )}
      </CardContent>

      {/* Image Preview Modal */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Preview Generated Image</DialogTitle>
          </DialogHeader>
          {previewImage && (
            <div className="space-y-4">
              <img
                src={previewImage}
                alt="Generated image preview"
                className="max-w-2xl max-h-96 mx-auto object-contain rounded-lg"
              />
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setPreviewImage(null)}>
                  Cancel
                </Button>
                <Button type="button" onClick={handlePreviewConfirm}>
                  <Check className="w-4 h-4 mr-2" />
                  Use This Image
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};